import { type UseQueryResult } from '@tanstack/react-query';
import { z } from 'zod';

import { useAuth } from '../../../auth/auth-context.js';
import { useApiQuery } from '../../../lib/api/hooks.js';
import { queryKeys } from '../../../lib/api/query-keys.js';
import { tenantAdminEndpoints } from './endpoints.js';
import { tenantAdminDetail } from './schemas.js';
import type { TenantAdminDetail } from './schemas.js';

const tenantOptionsSchema = z.array(
  z.object({
    id: z.string(),
    name: z.string(),
  }),
);

/**
 * Reads for the Tenant Admins module.
 *
 * The list is not here: `ResourceListPage` owns list fetching so that search,
 * sort, filtering, pagination and column selection are implemented once rather
 * than per resource. A module supplies the descriptor and the schema.
 *
 * What is here is the detail read and the tenant vocabulary the form needs.
 */

/** One tenant administrator, scoped to a tenant. */
export function useTenantAdmin(
  userId: string,
  tenantId: string,
): UseQueryResult<TenantAdminDetail> {
  return useApiQuery({
    queryKey: queryKeys.tenantAdmins.detail(userId),
    path: tenantAdminEndpoints.detail(userId),
    query: { tenantId },
    schema: tenantAdminDetail,
    enabled: userId !== '' && tenantId !== '',
  });
}

/** Active, non-deleted tenants for the dropdown picker.
 *
 * This endpoint is platform-operator only; tenant admins should not trigger it.
 */
export function useTenantOptions(): UseQueryResult<
  readonly { readonly id: string; readonly name: string }[]
> {
  const { user } = useAuth();
  const isPlatform = user !== null && user.tenantId === undefined;

  return useApiQuery({
    queryKey: ['tenants', 'reference', 'active-tenants'],
    path: '/v1/tenants/dropdown',
    query: { status: 'ACTIVE' },
    schema: tenantOptionsSchema,
    enabled: isPlatform,
  });
}
